/**
 * Single per-tool presentation declaration for the Work timeline.
 *
 * Composes existing contract name lists (object tools, openable routines,
 * session drafts, sub-agents, clarifications, plan tools) plus the label
 * rows that used to live in `packages/agents/src/ui/projects/timeline/toolPresentation.ts`.
 * Name-only. The few runtime overrides live in {@link resolveToolPresentation}.
 */

import type { MessageDescriptor } from 'react-intl';

import { draftResolutionOf } from '../../../../contracts/approval-kinds';
import {
  ASK_USER_TOOL_NAME,
  REQUEST_COMPUTER_LOGIN_TOOL_NAME,
} from '../../../../contracts/ask-user-details.types';
import type { ToolUseItem } from '../../../../contracts/harness.types';
import {
  GET_NOTE_VERIFICATION_ANALYSIS_TOOL_NAME,
  VERIFY_SUBAGENT_TOOL_NAMES,
} from '../../../../contracts/projects/VerifySubagentInlineBlock.utils';
import { OPENABLE_ROUTINE_TOOL_NAMES } from '../../../../contracts/workflows/lib/routineToolNames';
import type { Artifact } from '../../artifacts/state';
import { EVIDENCE_SUBAGENT_TOOL_NAMES } from '../../components/EvidenceSessionInlineBlock.utils';
import { CREATE_TODO_PLAN_TOOL_NAME, TODO_TOOL_NAME } from '../agent-todo/types';
import { OBJECT_TOOL_NAMES, objectWriteFor } from '../object-card/objectWrites';
import {
  ARTIFACT_REFS,
  artifactFromResultEnvelope,
  type ArtifactRefPorts,
  type ToolArtifactSpec,
} from './tool-artifact-refs';
import { toolLabelMessages } from './toolLabel.messages';

const INVOKE_AGENT_TOOL_NAME = 'invoke_agent';
const GENERATE_CONSULT_NOTE_TOOL_NAME = 'generate_consult_note';

export type ToolInteractionKind = 'clarification' | 'plan';

/** One member per bespoke card renderer; named after the renderer, never the tool. */
export type ToolCardKind =
  | 'object'
  | 'routine'
  | 'evidence-subagent'
  | 'verify-subagent'
  | 'computer-use'
  | 'child-run'
  | 'consult-note'
  | 'verify-analysis'
  | 'export-pdf'
  | 'export-csv'
  | 'export-excel'
  | 'export-pptx'
  | 'export-word'
  | 'export-to-word'
  | 'patient-evidence-pdf'
  | 'fill-pdf-form'
  | 'coverage-eligibility'
  | 'multiplayer-handoff'
  | 'clinical-task'
  | 'medical-calculator'
  | 'create-template'
  | 'open-template-editor'
  | 'pin-template'
  | 'unpin-template'
  | 'report-issue'
  | 'save-work-output'
  | 'record-note-push-hints'
  | 'update-word-library'
  | 'session-recording';

export type ToolIconName =
  | 'bar-chart-3'
  | 'calendar-clock'
  | 'calendar-days'
  | 'calendar-search'
  | 'file-spreadsheet'
  | 'file-text'
  | 'folder-minus'
  | 'folder-open'
  | 'folder-pen'
  | 'folder-plus'
  | 'folder-search'
  | 'flag'
  | 'hash'
  | 'link-2'
  | 'list-checks'
  | 'mail'
  | 'mic'
  | 'pin'
  | 'pin-off'
  | 'presentation'
  | 'rotate-ccw'
  | 'search'
  | 'session-merge'
  | 'share-2'
  | 'trash-2'
  | 'unlink'
  | 'user-round'
  | 'user-round-minus'
  | 'user-round-plus'
  | 'users-round'
  | 'x'
  | 'zap';

/**
 * Localised row labels for one tool. Each is a full ICU sentence so every
 * locale controls word order and plural forms; `count` is the number of
 * grouped same-name calls (1 for a single row), `done` how many finished.
 */
export interface ToolLabelSpec {
  /** Label while the call or group is running. */
  active: MessageDescriptor;
  /** Label once the call or group succeeded. */
  past: MessageDescriptor;
  /** Grouped label when only `done` of `count` calls finished. */
  pastPartial: MessageDescriptor;
  icon: ToolIconName;
}

/**
 * What one tool declares. Everything not declared falls to the default: a
 * plain row inside the Working block, no card, never opens the side panel.
 */
export interface ToolPresentationSpec {
  label?: ToolLabelSpec;
  /** Standalone, outside the Working block — the user must act on it. */
  interaction?: ToolInteractionKind;
  /** Opt-in rich object rendered beneath the Working block that holds the row. */
  card?: ToolCardKind;
  /**
   * Opens the side panel. The ref lives with the declaration so a tool
   * opens the panel iff it declares one — there is no second map to keep
   * in step.
   */
  artifact?: ToolArtifactSpec;
}

export interface ResolvedToolPresentation {
  axis: 'activity' | 'interaction';
  card?: ToolCardKind;
  artifact?: ToolArtifactSpec;
  label?: ToolLabelSpec;
  interaction?: ToolInteractionKind;
  /** Derived, never declared: an activity row whose card (if any) has a bulk form, or an object write. */
  groupable: boolean;
}

/** Card kinds with a bulk rendering for a same-name run; object writes group via `objectWriteFor`. */
const GROUPABLE_CARD_KINDS: ReadonlySet<ToolCardKind> = new Set(['routine']);

const READ_CLINICIAN_NOTE: ToolLabelSpec = {
  active: toolLabelMessages.readClinicianNoteActive,
  past: toolLabelMessages.readClinicianNotePast,
  pastPartial: toolLabelMessages.readClinicianNotePastPartial,
  icon: 'file-text',
};

const EDIT_CLINICIAN_NOTE: ToolLabelSpec = {
  active: toolLabelMessages.updatedClinicianNoteActive,
  past: toolLabelMessages.updatedClinicianNotePast,
  pastPartial: toolLabelMessages.updatedClinicianNotePastPartial,
  icon: 'file-text',
};

const LABEL_BY_TOOL: Readonly<Record<string, ToolLabelSpec>> = {
  create_patient_profile: {
    active: toolLabelMessages.createdPatientActive,
    past: toolLabelMessages.createdPatientPast,
    pastPartial: toolLabelMessages.createdPatientPastPartial,
    icon: 'user-round',
  },
  update_patient_context: {
    active: toolLabelMessages.updatedPatientActive,
    past: toolLabelMessages.updatedPatientPast,
    pastPartial: toolLabelMessages.updatedPatientPastPartial,
    icon: 'user-round',
  },
  merge_patient_profiles: {
    active: toolLabelMessages.mergedPatientProfilesActive,
    past: toolLabelMessages.mergedPatientProfilesPast,
    pastPartial: toolLabelMessages.mergedPatientProfilesPastPartial,
    icon: 'session-merge',
  },
  upload_patient_document: {
    active: toolLabelMessages.uploadedPatientDocumentActive,
    past: toolLabelMessages.uploadedPatientDocumentPast,
    pastPartial: toolLabelMessages.uploadedPatientDocumentPastPartial,
    icon: 'file-text',
  },
  list_patient_documents: {
    active: toolLabelMessages.listedPatientDocumentsActive,
    past: toolLabelMessages.listedPatientDocumentsPast,
    pastPartial: toolLabelMessages.listedPatientDocumentsPastPartial,
    icon: 'file-text',
  },
  get_patient_document: {
    active: toolLabelMessages.readPatientDocumentActive,
    past: toolLabelMessages.readPatientDocumentPast,
    pastPartial: toolLabelMessages.readPatientDocumentPastPartial,
    icon: 'file-text',
  },
  create_session: {
    active: toolLabelMessages.createdSessionActive,
    past: toolLabelMessages.createdSessionPast,
    pastPartial: toolLabelMessages.createdSessionPastPartial,
    icon: 'calendar-days',
  },
  create_session_for_member: {
    active: toolLabelMessages.createdTeammateSessionActive,
    past: toolLabelMessages.createdTeammateSessionPast,
    pastPartial: toolLabelMessages.createdTeammateSessionPastPartial,
    icon: 'calendar-days',
  },
  delete_session: {
    active: toolLabelMessages.deletedSessionActive,
    past: toolLabelMessages.deletedSessionPast,
    pastPartial: toolLabelMessages.deletedSessionPastPartial,
    icon: 'trash-2',
  },
  email_session_content: {
    active: toolLabelMessages.emailedSessionEmailActive,
    past: toolLabelMessages.emailedSessionEmailPast,
    pastPartial: toolLabelMessages.emailedSessionEmailPastPartial,
    icon: 'mail',
  },
  delete_template: {
    active: toolLabelMessages.deletedTemplateActive,
    past: toolLabelMessages.deletedTemplatePast,
    pastPartial: toolLabelMessages.deletedTemplatePastPartial,
    icon: 'trash-2',
  },
  open_template_editor: {
    active: toolLabelMessages.openedTemplateEditorActive,
    past: toolLabelMessages.openedTemplateEditorPast,
    pastPartial: toolLabelMessages.openedTemplateEditorPastPartial,
    icon: 'file-text',
  },
  pin_template: {
    active: toolLabelMessages.pinnedTemplateActive,
    past: toolLabelMessages.pinnedTemplatePast,
    pastPartial: toolLabelMessages.pinnedTemplatePastPartial,
    icon: 'pin',
  },
  unpin_template: {
    active: toolLabelMessages.unpinnedTemplateActive,
    past: toolLabelMessages.unpinnedTemplatePast,
    pastPartial: toolLabelMessages.unpinnedTemplatePastPartial,
    icon: 'pin-off',
  },
  generate_prechart_document: {
    active: toolLabelMessages.generatedPrechartActive,
    past: toolLabelMessages.generatedPrechartPast,
    pastPartial: toolLabelMessages.generatedPrechartPastPartial,
    icon: 'file-text',
  },
  get_template_usage: {
    active: toolLabelMessages.checkedTemplateUsageActive,
    past: toolLabelMessages.checkedTemplateUsagePast,
    pastPartial: toolLabelMessages.checkedTemplateUsagePastPartial,
    icon: 'bar-chart-3',
  },
  list_templates_with_usage: {
    active: toolLabelMessages.listedTemplateUsageActive,
    past: toolLabelMessages.listedTemplateUsagePast,
    pastPartial: toolLabelMessages.listedTemplateUsagePastPartial,
    icon: 'bar-chart-3',
  },
  get_template_performance: {
    active: toolLabelMessages.analysedTemplatePerformanceActive,
    past: toolLabelMessages.analysedTemplatePerformancePast,
    pastPartial: toolLabelMessages.analysedTemplatePerformancePastPartial,
    icon: 'bar-chart-3',
  },
  team_session_insights: {
    active: toolLabelMessages.summarisedTeamInsightsActive,
    past: toolLabelMessages.summarisedTeamInsightsPast,
    pastPartial: toolLabelMessages.summarisedTeamInsightsPastPartial,
    icon: 'bar-chart-3',
  },
  get_session_codes: {
    active: toolLabelMessages.fetchedClinicalCodesActive,
    past: toolLabelMessages.fetchedClinicalCodesPast,
    pastPartial: toolLabelMessages.fetchedClinicalCodesPastPartial,
    icon: 'hash',
  },
  confirm_session_codes: {
    active: toolLabelMessages.confirmedClinicalCodesActive,
    past: toolLabelMessages.confirmedClinicalCodesPast,
    pastPartial: toolLabelMessages.confirmedClinicalCodesPastPartial,
    icon: 'hash',
  },
  reject_session_code: {
    active: toolLabelMessages.rejectedClinicalCodeActive,
    past: toolLabelMessages.rejectedClinicalCodePast,
    pastPartial: toolLabelMessages.rejectedClinicalCodePastPartial,
    icon: 'hash',
  },
  add_session_code: {
    active: toolLabelMessages.addedClinicalCodeActive,
    past: toolLabelMessages.addedClinicalCodePast,
    pastPartial: toolLabelMessages.addedClinicalCodePastPartial,
    icon: 'hash',
  },
  get_team_session_configuration: {
    active: toolLabelMessages.fetchedTeamSessionSharingActive,
    past: toolLabelMessages.fetchedTeamSessionSharingPast,
    pastPartial: toolLabelMessages.fetchedTeamSessionSharingPastPartial,
    icon: 'users-round',
  },
  update_team_session_configuration: {
    active: toolLabelMessages.updatedTeamSessionSharingActive,
    past: toolLabelMessages.updatedTeamSessionSharingPast,
    pastPartial: toolLabelMessages.updatedTeamSessionSharingPastPartial,
    icon: 'users-round',
  },
  set_session_title: {
    active: toolLabelMessages.setSessionTitleActive,
    past: toolLabelMessages.setSessionTitlePast,
    pastPartial: toolLabelMessages.setSessionTitlePastPartial,
    icon: 'calendar-days',
  },
  link_session_to_patient: {
    active: toolLabelMessages.linkedSessionActive,
    past: toolLabelMessages.linkedSessionPast,
    pastPartial: toolLabelMessages.linkedSessionPastPartial,
    icon: 'link-2',
  },
  add_session_context_text: {
    active: toolLabelMessages.addedContextNoteActive,
    past: toolLabelMessages.addedContextNotePast,
    pastPartial: toolLabelMessages.addedContextNotePastPartial,
    icon: 'file-text',
  },
  create_session_context_document: {
    active: toolLabelMessages.addedContextDocumentActive,
    past: toolLabelMessages.addedContextDocumentPast,
    pastPartial: toolLabelMessages.addedContextDocumentPastPartial,
    icon: 'file-text',
  },
  update_session_context_document: {
    active: toolLabelMessages.updatedContextDocumentActive,
    past: toolLabelMessages.updatedContextDocumentPast,
    pastPartial: toolLabelMessages.updatedContextDocumentPastPartial,
    icon: 'file-text',
  },
  delete_session_context_document: {
    active: toolLabelMessages.deletedContextDocumentActive,
    past: toolLabelMessages.deletedContextDocumentPast,
    pastPartial: toolLabelMessages.deletedContextDocumentPastPartial,
    icon: 'file-text',
  },
  list_session_context_documents: {
    active: toolLabelMessages.listedContextDocumentActive,
    past: toolLabelMessages.listedContextDocumentPast,
    pastPartial: toolLabelMessages.listedContextDocumentPastPartial,
    icon: 'file-text',
  },
  get_session_context_document: {
    active: toolLabelMessages.readContextDocumentActive,
    past: toolLabelMessages.readContextDocumentPast,
    pastPartial: toolLabelMessages.readContextDocumentPastPartial,
    icon: 'file-text',
  },
  create_clinical_task: {
    active: toolLabelMessages.createdTaskActive,
    past: toolLabelMessages.createdTaskPast,
    pastPartial: toolLabelMessages.createdTaskPastPartial,
    icon: 'list-checks',
  },
  search_patient_profiles: {
    active: toolLabelMessages.searchedPatientProfilesActive,
    past: toolLabelMessages.searchedPatientProfilesPast,
    pastPartial: toolLabelMessages.searchedPatientProfilesPastPartial,
    icon: 'search',
  },
  get_patient_summary: {
    active: toolLabelMessages.loadedPatientContextActive,
    past: toolLabelMessages.loadedPatientContextPast,
    pastPartial: toolLabelMessages.loadedPatientContextPastPartial,
    icon: 'user-round',
  },
  get_template_and_preferences: {
    active: toolLabelMessages.loadedTemplatePreferencesActive,
    past: toolLabelMessages.loadedTemplatePreferencesPast,
    pastPartial: toolLabelMessages.loadedTemplatePreferencesPastPartial,
    icon: 'file-text',
  },
  get_sessions_by_date: {
    active: toolLabelMessages.searchedSessionsActive,
    past: toolLabelMessages.searchedSessionsPast,
    pastPartial: toolLabelMessages.searchedSessionsPastPartial,
    icon: 'calendar-search',
  },
  list_sessions: {
    active: toolLabelMessages.searchedSessionsActive,
    past: toolLabelMessages.searchedSessionsPast,
    pastPartial: toolLabelMessages.searchedSessionsPastPartial,
    icon: 'calendar-search',
  },
  get_transcript: {
    active: toolLabelMessages.gotTranscriptActive,
    past: toolLabelMessages.gotTranscriptPast,
    pastPartial: toolLabelMessages.gotTranscriptPastPartial,
    icon: 'file-text',
  },
  open_artifact: {
    active: toolLabelMessages.openedArtifactActive,
    past: toolLabelMessages.openedArtifactPast,
    pastPartial: toolLabelMessages.openedArtifactPastPartial,
    icon: 'folder-open',
  },
  list_mergeable_sessions: {
    active: toolLabelMessages.foundMergeableSessionsActive,
    past: toolLabelMessages.foundMergeableSessionsPast,
    pastPartial: toolLabelMessages.foundMergeableSessionsPastPartial,
    icon: 'calendar-search',
  },
  merge_sessions: {
    active: toolLabelMessages.mergedSessionsActive,
    past: toolLabelMessages.mergedSessionsPast,
    pastPartial: toolLabelMessages.mergedSessionsPastPartial,
    icon: 'session-merge',
  },
  unlink_merged_sessions: {
    active: toolLabelMessages.unlinkedMergedSessionsActive,
    past: toolLabelMessages.unlinkedMergedSessionsPast,
    pastPartial: toolLabelMessages.unlinkedMergedSessionsPastPartial,
    icon: 'unlink',
  },
  discard_session_merge_suggestion: {
    active: toolLabelMessages.dismissedMergeSuggestionActive,
    past: toolLabelMessages.dismissedMergeSuggestionPast,
    pastPartial: toolLabelMessages.dismissedMergeSuggestionPastPartial,
    icon: 'x',
  },
  list_team_members: {
    active: toolLabelMessages.lookedUpTeamMembersActive,
    past: toolLabelMessages.lookedUpTeamMembersPast,
    pastPartial: toolLabelMessages.lookedUpTeamMembersPastPartial,
    icon: 'user-round',
  },
  share_session: {
    active: toolLabelMessages.sharedSessionActive,
    past: toolLabelMessages.sharedSessionPast,
    pastPartial: toolLabelMessages.sharedSessionPastPartial,
    icon: 'share-2',
  },
  unshare_session: {
    active: toolLabelMessages.revokedSessionAccessActive,
    past: toolLabelMessages.revokedSessionAccessPast,
    pastPartial: toolLabelMessages.revokedSessionAccessPastPartial,
    icon: 'share-2',
  },
  list_session_shares: {
    active: toolLabelMessages.listedSessionSharesActive,
    past: toolLabelMessages.listedSessionSharesPast,
    pastPartial: toolLabelMessages.listedSessionSharesPastPartial,
    icon: 'users-round',
  },
  invite_team_member: {
    active: toolLabelMessages.invitedTeamMemberActive,
    past: toolLabelMessages.invitedTeamMemberPast,
    pastPartial: toolLabelMessages.invitedTeamMemberPastPartial,
    icon: 'user-round-plus',
  },
  remove_team_member: {
    active: toolLabelMessages.removedTeamMemberActive,
    past: toolLabelMessages.removedTeamMemberPast,
    pastPartial: toolLabelMessages.removedTeamMemberPastPartial,
    icon: 'user-round-minus',
  },
  share_template: {
    active: toolLabelMessages.sharedTemplateActive,
    past: toolLabelMessages.sharedTemplatePast,
    pastPartial: toolLabelMessages.sharedTemplatePastPartial,
    icon: 'share-2',
  },
  unshare_template: {
    active: toolLabelMessages.revokedTemplateAccessActive,
    past: toolLabelMessages.revokedTemplateAccessPast,
    pastPartial: toolLabelMessages.revokedTemplateAccessPastPartial,
    icon: 'share-2',
  },
  generate_csv: {
    active: toolLabelMessages.generatedCSVActive,
    past: toolLabelMessages.generatedCSVPast,
    pastPartial: toolLabelMessages.generatedCSVPastPartial,
    icon: 'file-spreadsheet',
  },
  generate_excel: {
    active: toolLabelMessages.generatedSpreadsheetActive,
    past: toolLabelMessages.generatedSpreadsheetPast,
    pastPartial: toolLabelMessages.generatedSpreadsheetPastPartial,
    icon: 'file-spreadsheet',
  },
  generate_pptx: {
    active: toolLabelMessages.generatedPresentationActive,
    past: toolLabelMessages.generatedPresentationPast,
    pastPartial: toolLabelMessages.generatedPresentationPastPartial,
    icon: 'presentation',
  },
  get_presentation_slides: {
    active: toolLabelMessages.readPresentationSlidesActive,
    past: toolLabelMessages.readPresentationSlidesPast,
    pastPartial: toolLabelMessages.readPresentationSlidesPastPartial,
    icon: 'presentation',
  },
  edit_slide: {
    active: toolLabelMessages.editedSlideActive,
    past: toolLabelMessages.editedSlidePast,
    pastPartial: toolLabelMessages.editedSlidePastPartial,
    icon: 'presentation',
  },
  add_slides_to_presentation: {
    active: toolLabelMessages.addedSlidesActive,
    past: toolLabelMessages.addedSlidesPast,
    pastPartial: toolLabelMessages.addedSlidesPastPartial,
    icon: 'presentation',
  },
  delete_slide: {
    active: toolLabelMessages.deletedSlideActive,
    past: toolLabelMessages.deletedSlidePast,
    pastPartial: toolLabelMessages.deletedSlidePastPartial,
    icon: 'presentation',
  },
  generate_word: {
    active: toolLabelMessages.generatedDocumentActive,
    past: toolLabelMessages.generatedDocumentPast,
    pastPartial: toolLabelMessages.generatedDocumentPastPartial,
    icon: 'file-text',
  },
  export_to_word: {
    active: toolLabelMessages.exportedWordDocumentActive,
    past: toolLabelMessages.exportedWordDocumentPast,
    pastPartial: toolLabelMessages.exportedWordDocumentPastPartial,
    icon: 'file-text',
  },
  export_to_pdf: {
    active: toolLabelMessages.exportedPDFActive,
    past: toolLabelMessages.exportedPDFPast,
    pastPartial: toolLabelMessages.exportedPDFPastPartial,
    icon: 'file-text',
  },
  create_patient_evidence_pdf: {
    active: toolLabelMessages.createdEvidencePDFActive,
    past: toolLabelMessages.createdEvidencePDFPast,
    pastPartial: toolLabelMessages.createdEvidencePDFPastPartial,
    icon: 'file-text',
  },
  fill_pdf_form: {
    active: toolLabelMessages.filledPDFFormActive,
    past: toolLabelMessages.filledPDFFormPast,
    pastPartial: toolLabelMessages.filledPDFFormPastPartial,
    icon: 'file-text',
  },
  fill_form_from_upload: {
    active: toolLabelMessages.filledFormActive,
    past: toolLabelMessages.filledFormPast,
    pastPartial: toolLabelMessages.filledFormPastPartial,
    icon: 'file-text',
  },
  fill_word_form: {
    active: toolLabelMessages.filledWordFormActive,
    past: toolLabelMessages.filledWordFormPast,
    pastPartial: toolLabelMessages.filledWordFormPastPartial,
    icon: 'file-text',
  },
  list_word_form: {
    active: toolLabelMessages.listedFormFieldsActive,
    past: toolLabelMessages.listedFormFieldsPast,
    pastPartial: toolLabelMessages.listedFormFieldsPastPartial,
    icon: 'file-text',
  },
  fill_excel_template: {
    active: toolLabelMessages.filledExcelTemplateActive,
    past: toolLabelMessages.filledExcelTemplatePast,
    pastPartial: toolLabelMessages.filledExcelTemplatePastPartial,
    icon: 'file-spreadsheet',
  },
  list_excel_template: {
    active: toolLabelMessages.listedExcelLayoutActive,
    past: toolLabelMessages.listedExcelLayoutPast,
    pastPartial: toolLabelMessages.listedExcelLayoutPastPartial,
    icon: 'file-spreadsheet',
  },
  'evidence.list_collections': {
    active: toolLabelMessages.listedLibraryCollectionsActive,
    past: toolLabelMessages.listedLibraryCollectionsPast,
    pastPartial: toolLabelMessages.listedLibraryCollectionsPastPartial,
    icon: 'folder-search',
  },
  'evidence.get_collection': {
    active: toolLabelMessages.loadedLibraryCollectionActive,
    past: toolLabelMessages.loadedLibraryCollectionPast,
    pastPartial: toolLabelMessages.loadedLibraryCollectionPastPartial,
    icon: 'folder-open',
  },
  'evidence.create_collection': {
    active: toolLabelMessages.createdLibraryCollectionActive,
    past: toolLabelMessages.createdLibraryCollectionPast,
    pastPartial: toolLabelMessages.createdLibraryCollectionPastPartial,
    icon: 'folder-plus',
  },
  'evidence.update_collection': {
    active: toolLabelMessages.updatedLibraryCollectionActive,
    past: toolLabelMessages.updatedLibraryCollectionPast,
    pastPartial: toolLabelMessages.updatedLibraryCollectionPastPartial,
    icon: 'folder-pen',
  },
  'evidence.delete_collection': {
    active: toolLabelMessages.deletedLibraryCollectionActive,
    past: toolLabelMessages.deletedLibraryCollectionPast,
    pastPartial: toolLabelMessages.deletedLibraryCollectionPastPartial,
    icon: 'folder-minus',
  },
  'evidence.save_work_output': {
    active: toolLabelMessages.savedWorkArtefactActive,
    past: toolLabelMessages.savedWorkArtefactPast,
    pastPartial: toolLabelMessages.savedWorkArtefactPastPartial,
    icon: 'folder-open',
  },
  get_session_context: READ_CLINICIAN_NOTE,
  get_session_clinician_notes: READ_CLINICIAN_NOTE,
  edit_session_context: EDIT_CLINICIAN_NOTE,
  edit_session_clinician_notes: EDIT_CLINICIAN_NOTE,
  edit_consult_note: {
    active: toolLabelMessages.editedConsultNoteActive,
    past: toolLabelMessages.editedConsultNotePast,
    pastPartial: toolLabelMessages.editedConsultNotePastPartial,
    icon: 'file-text',
  },
  get_consult_note: {
    active: toolLabelMessages.readConsultNoteActive,
    past: toolLabelMessages.readConsultNotePast,
    pastPartial: toolLabelMessages.readConsultNotePastPartial,
    icon: 'file-text',
  },
  'ui.session_recording': {
    active: toolLabelMessages.updatedTranscriptionActive,
    past: toolLabelMessages.updatedTranscriptionPast,
    pastPartial: toolLabelMessages.updatedTranscriptionPastPartial,
    icon: 'mic',
  },
  report_issue: {
    active: toolLabelMessages.reportedIssueActive,
    past: toolLabelMessages.reportedIssuePast,
    pastPartial: toolLabelMessages.reportedIssuePastPartial,
    icon: 'flag',
  },
  transcribe_attachment: {
    active: toolLabelMessages.transcribedAttachmentActive,
    past: toolLabelMessages.transcribedAttachmentPast,
    pastPartial: toolLabelMessages.transcribedAttachmentPastPartial,
    icon: 'mic',
  },
  generate_consult_note: {
    active: toolLabelMessages.generatedConsultNoteActive,
    past: toolLabelMessages.generatedConsultNotePast,
    pastPartial: toolLabelMessages.generatedConsultNotePastPartial,
    icon: 'file-text',
  },
  answer_with_evidence: {
    active: toolLabelMessages.checkedLiteratureActive,
    past: toolLabelMessages.checkedLiteraturePast,
    pastPartial: toolLabelMessages.checkedLiteraturePastPartial,
    icon: 'search',
  },
  lookup_evidence: {
    active: toolLabelMessages.checkedLiteratureActive,
    past: toolLabelMessages.checkedLiteraturePast,
    pastPartial: toolLabelMessages.checkedLiteraturePastPartial,
    icon: 'search',
  },
  root_cause_analyzer: {
    active: toolLabelMessages.ranVerificationActive,
    past: toolLabelMessages.ranVerificationPast,
    pastPartial: toolLabelMessages.ranVerificationPastPartial,
    icon: 'list-checks',
  },
  create_workflow: {
    active: toolLabelMessages.createdRoutineActive,
    past: toolLabelMessages.createdRoutinePast,
    pastPartial: toolLabelMessages.createdRoutinePastPartial,
    icon: 'rotate-ccw',
  },
  create_routine: {
    active: toolLabelMessages.createdRoutineActive,
    past: toolLabelMessages.createdRoutinePast,
    pastPartial: toolLabelMessages.createdRoutinePastPartial,
    icon: 'rotate-ccw',
  },
  create_scheduled_workflow: {
    active: toolLabelMessages.createdRoutineActive,
    past: toolLabelMessages.createdRoutinePast,
    pastPartial: toolLabelMessages.createdRoutinePastPartial,
    icon: 'rotate-ccw',
  },
  create_scheduled_routine: {
    active: toolLabelMessages.createdRoutineActive,
    past: toolLabelMessages.createdRoutinePast,
    pastPartial: toolLabelMessages.createdRoutinePastPartial,
    icon: 'rotate-ccw',
  },
  update_workflow: {
    active: toolLabelMessages.updatedRoutineActive,
    past: toolLabelMessages.updatedRoutinePast,
    pastPartial: toolLabelMessages.updatedRoutinePastPartial,
    icon: 'rotate-ccw',
  },
  update_routine: {
    active: toolLabelMessages.updatedRoutineActive,
    past: toolLabelMessages.updatedRoutinePast,
    pastPartial: toolLabelMessages.updatedRoutinePastPartial,
    icon: 'rotate-ccw',
  },
  schedule_workflow: {
    active: toolLabelMessages.scheduledRoutineActive,
    past: toolLabelMessages.scheduledRoutinePast,
    pastPartial: toolLabelMessages.scheduledRoutinePastPartial,
    icon: 'calendar-clock',
  },
  schedule_routine: {
    active: toolLabelMessages.scheduledRoutineActive,
    past: toolLabelMessages.scheduledRoutinePast,
    pastPartial: toolLabelMessages.scheduledRoutinePastPartial,
    icon: 'calendar-clock',
  },
  set_workflow_event_triggers: {
    active: toolLabelMessages.setRoutineTriggersActive,
    past: toolLabelMessages.setRoutineTriggersPast,
    pastPartial: toolLabelMessages.setRoutineTriggersPastPartial,
    icon: 'zap',
  },
  set_routine_event_triggers: {
    active: toolLabelMessages.setRoutineTriggersActive,
    past: toolLabelMessages.setRoutineTriggersPast,
    pastPartial: toolLabelMessages.setRoutineTriggersPastPartial,
    icon: 'zap',
  },
  list_directory: {
    active: toolLabelMessages.listedDirectoryActive,
    past: toolLabelMessages.listedDirectoryPast,
    pastPartial: toolLabelMessages.listedDirectoryPastPartial,
    icon: 'folder-open',
  },
  read_file: {
    active: toolLabelMessages.readFileActive,
    past: toolLabelMessages.readFilePast,
    pastPartial: toolLabelMessages.readFilePastPartial,
    icon: 'file-text',
  },
  search_files: {
    active: toolLabelMessages.searchedFilesActive,
    past: toolLabelMessages.searchedFilesPast,
    pastPartial: toolLabelMessages.searchedFilesPastPartial,
    icon: 'folder-search',
  },
  stat_file: {
    active: toolLabelMessages.checkedFileDetailsActive,
    past: toolLabelMessages.checkedFileDetailsPast,
    pastPartial: toolLabelMessages.checkedFileDetailsPastPartial,
    icon: 'file-text',
  },
  create_file: {
    active: toolLabelMessages.createdFileActive,
    past: toolLabelMessages.createdFilePast,
    pastPartial: toolLabelMessages.createdFilePastPartial,
    icon: 'folder-plus',
  },
  rename_file: {
    active: toolLabelMessages.renamedFileActive,
    past: toolLabelMessages.renamedFilePast,
    pastPartial: toolLabelMessages.renamedFilePastPartial,
    icon: 'folder-pen',
  },
};

/**
 * Direct-harness tools that render through `renderEvidenceToolUse`.
 * Kinds are named after the renderer, not the tool.
 */
const EVIDENCE_ADAPTER_CARDS: Readonly<Record<string, ToolCardKind>> = {
  export_to_pdf: 'export-pdf',
  generate_csv: 'export-csv',
  generate_excel: 'export-excel',
  fill_excel_template: 'export-excel',
  generate_pptx: 'export-pptx',
  generate_word: 'export-word',
  fill_word_form: 'export-word',
  export_to_word: 'export-to-word',
  create_patient_evidence_pdf: 'patient-evidence-pdf',
  fill_pdf_form: 'fill-pdf-form',
  fill_form_from_upload: 'fill-pdf-form',
  share_session: 'multiplayer-handoff',
  unshare_session: 'multiplayer-handoff',
  invite_team_member: 'multiplayer-handoff',
  remove_team_member: 'multiplayer-handoff',
  share_template: 'multiplayer-handoff',
  unshare_template: 'multiplayer-handoff',
  create_clinical_task: 'clinical-task',
  update_task_status: 'clinical-task',
  medical_calculator: 'medical-calculator',
  create_template: 'create-template',
  open_template_editor: 'open-template-editor',
  pin_template: 'pin-template',
  unpin_template: 'unpin-template',
  'evidence.save_work_output': 'save-work-output',
  record_note_push_hints: 'record-note-push-hints',
  report_issue: 'report-issue',
  update_word_library: 'update-word-library',
  'ui.session_recording': 'session-recording',
  'integrations.apps.read': 'coverage-eligibility',
  'integrations.apps.write': 'coverage-eligibility',
};

const assign = (
  target: Record<string, ToolPresentationSpec>,
  name: string,
  patch: ToolPresentationSpec
): void => {
  const current = target[name];
  target[name] = current ? { ...current, ...patch } : patch;
};

const buildRegistry = (): Record<string, ToolPresentationSpec> => {
  const registry: Record<string, ToolPresentationSpec> = {};

  for (const [name, label] of Object.entries(LABEL_BY_TOOL)) {
    assign(registry, name, { label });
  }

  for (const name of OBJECT_TOOL_NAMES) {
    assign(registry, name, { card: 'object' });
  }
  for (const name of OPENABLE_ROUTINE_TOOL_NAMES) {
    assign(registry, name, { card: 'routine' });
  }
  for (const name of EVIDENCE_SUBAGENT_TOOL_NAMES) {
    assign(registry, name, { card: 'evidence-subagent' });
  }
  for (const name of VERIFY_SUBAGENT_TOOL_NAMES) {
    assign(registry, name, { card: 'verify-subagent' });
  }
  for (const [name, card] of Object.entries(EVIDENCE_ADAPTER_CARDS)) {
    assign(registry, name, { card });
  }
  assign(registry, INVOKE_AGENT_TOOL_NAME, { card: 'child-run' });
  assign(registry, GENERATE_CONSULT_NOTE_TOOL_NAME, { card: 'consult-note' });
  assign(registry, GET_NOTE_VERIFICATION_ANALYSIS_TOOL_NAME, {
    card: 'verify-analysis',
  });

  assign(registry, ASK_USER_TOOL_NAME, { interaction: 'clarification' });
  assign(registry, REQUEST_COMPUTER_LOGIN_TOOL_NAME, { interaction: 'clarification' });
  assign(registry, TODO_TOOL_NAME, { interaction: 'plan' });
  assign(registry, CREATE_TODO_PLAN_TOOL_NAME, { interaction: 'plan' });

  for (const [name, spec] of Object.entries(ARTIFACT_REFS)) {
    assign(registry, name, { artifact: spec });
  }

  return registry;
};

export const TOOL_PRESENTATION_REGISTRY: Readonly<Record<string, ToolPresentationSpec>> =
  buildRegistry();

export type ResolveToolPresentationItem = Pick<ToolUseItem, 'name'> &
  Partial<Pick<ToolUseItem, 'status' | 'isComputerUse' | 'childAgentRunId' | 'approval'>>;

/**
 * A draft the clinician discarded. Its card becomes a receipt in the thread; as activity its denied call would be a failure, which
 * the Working block hides.
 */
const isDraftReceipt = ({ approval }: ResolveToolPresentationItem): boolean =>
  draftResolutionOf(approval) !== null;

/**
 * The name entry plus the facts that live on the ITEM rather than the tool: a
 * call awaiting approval, or a draft that became a receipt, is an interaction
 * whatever its tool, and a computer-use call or an un-registered child run has
 * a card by construction. Cards never change the axis — they render beneath the
 * Working block.
 */
export const resolveToolPresentation = (
  item: ResolveToolPresentationItem
): ResolvedToolPresentation => {
  const entry = TOOL_PRESENTATION_REGISTRY[item.name];
  const card = item.isComputerUse
    ? 'computer-use'
    : item.childAgentRunId && entry === undefined
      ? 'child-run'
      : entry?.card;
  const axis =
    item.status === 'pending_approval' || entry?.interaction || isDraftReceipt(item)
      ? 'interaction'
      : 'activity';
  const groupable =
    axis === 'activity' &&
    (card === undefined ||
      GROUPABLE_CARD_KINDS.has(card) ||
      objectWriteFor(item.name) !== undefined);

  return {
    axis,
    groupable,
    ...(card !== undefined && { card }),
    ...(entry?.artifact !== undefined && { artifact: entry.artifact }),
    ...(entry?.label !== undefined && { label: entry.label }),
    ...(entry?.interaction !== undefined && { interaction: entry.interaction }),
  };
};

/**
 * Derive the artifact reference (if any) a completed tool use produced.
 * A tool that declares an `artifact` resolves through it; any other tool
 * opens only what its result's `artifact` envelope names. `undefined` for
 * malformed inputs (missing required ids) — the row renders without an
 * "Open" button rather than crashing or linking the wrong artifact.
 */
export const artifactFromToolUse = (
  item: ToolUseItem,
  ports: ArtifactRefPorts
): Artifact | undefined => {
  const declared = resolveToolPresentation(item).artifact;
  return declared ? declared.ref(item, ports) : artifactFromResultEnvelope(item, ports);
};
